<script setup>
import { onMounted, ref, computed, watch, nextTick, onBeforeMount } from 'vue';
import { NavSecondaryMenu } from '@dashhudson/dashing-ui/nav-secondary-menu';
import { useSecondaryNavigationPanel } from '@dashhudson/dashing-ui/secondary-navigation-panel';
import { useRoute } from 'vue-router';
import pick from 'lodash/pick';
import isEqual from 'lodash/isEqual';

const props = defineProps({
  menuItems: { type: Array, required: true },
  loading: { type: Boolean, default: undefined },
  selected: { type: Array, default: undefined },
});

const emit = defineEmits(['update:selected']);

const route = useRoute();

const internalSelected = ref(null);
const expandedKeys = ref({});

const menuItemsWithKeys = computed(() =>
  props.menuItems
    .filter((item) => item.visible !== false)
    .map((item, i) => {
      const itemWithKey = {
        ...item,
        key: item.key ?? `${i}`,
      };
      if (item.items) {
        itemWithKey.items = item.items.map((subItem, index) => ({
          ...subItem,
          key: subItem.key ?? `${itemWithKey.key}_${index}`,
        }));
      }
      return itemWithKey;
    }),
);

function areParamsLooselyEqual(left, right) {
  const leftKeys = Object.keys(left || {});
  const rightKeys = Object.keys(right || {});

  if (leftKeys.length !== rightKeys.length) {
    return false;
  }

  if (leftKeys.length === 0 && rightKeys.length === 0) {
    return true;
  }

  return leftKeys.every((key) => {
    const leftValue = left[key];
    const rightValue = right[key];

    // Convert both to strings for loose equality comparison
    return String(leftValue) === String(rightValue);
  });
}

function doesMenuItemMatchRoute(menuItem) {
  const routeNamesMatch = menuItem?.route?.name === route.name;

  // If route names match, also verify params match
  if (routeNamesMatch) {
    return areParamsLooselyEqual(
      menuItem.route.params,
      pick(route.params, Object.keys(menuItem.route.params ?? {})),
    );
  }

  // If route names don't match but menuItem has childNames, check if current route is a child
  if (menuItem?.childNames?.includes(route.name)) {
    // For child routes, still verify params match if they're defined
    return areParamsLooselyEqual(
      menuItem.route?.params,
      pick(route.params, Object.keys(menuItem.route?.params ?? {})),
    );
  }

  return false;
}

function doesMenuItemOrChildrenMatchRoute(menuItem) {
  return (
    doesMenuItemMatchRoute(menuItem) ||
    menuItem?.items?.some((childItem) => doesMenuItemMatchRoute(childItem))
  );
}

function initExpandedKeys() {
  const expandedKeysMap = {};
  menuItemsWithKeys.value.forEach((item) => {
    if (item.items) {
      if (item.preExpand) {
        expandedKeysMap[item.key] = true;
      }
      item.items.forEach((subItem) => {
        if (doesMenuItemMatchRoute(subItem)) {
          expandedKeysMap[item.key] = true;
        }

        if (subItem.childNames) {
          subItem.childNames.forEach((childName) => {
            if (childName === route.name) {
              expandedKeysMap[item.key] = true;
            }
          });
        }
      });
    }
  });
  return expandedKeysMap;
}

function getTopMenuItemsMatchingCurrentRoute() {
  return menuItemsWithKeys.value.filter((menuItem) => doesMenuItemOrChildrenMatchRoute(menuItem));
}

function getActiveMenuItem() {
  // First check top-level items
  const topLevelMatch = menuItemsWithKeys.value.find((menuItem) =>
    doesMenuItemMatchRoute(menuItem),
  );

  if (topLevelMatch) {
    return topLevelMatch;
  }

  // Then check child items
  const childMatch = menuItemsWithKeys.value
    .flatMap((menuItem) => menuItem.items || [])
    .find((childItem) => doesMenuItemMatchRoute(childItem));

  return childMatch || null;
}

function expandItemMatchingRoute() {
  const routeMenuItems = getTopMenuItemsMatchingCurrentRoute();

  const routeExpandedKeys = routeMenuItems.reduce((acc, menuItem) => {
    acc[menuItem.key] = true;
    return acc;
  }, {});

  const preExpandedKeys = {};
  menuItemsWithKeys.value.forEach((item) => {
    if (item.items && item.preExpand) {
      preExpandedKeys[item.key] = true;
    }
  });

  expandedKeys.value = {
    ...expandedKeys.value,
    ...preExpandedKeys,
    ...routeExpandedKeys,
  };

  // Find the actual active menu item and scroll to it
  const activeMenuItem = getActiveMenuItem();
  const currentSelection = internalSelected.value?.[0];

  if (activeMenuItem) {
    // Only update selection if it's actually different
    if (!currentSelection || !isEqual(currentSelection, activeMenuItem)) {
      internalSelected.value = [activeMenuItem];
    }

    nextTick(() => {
      const target = document.querySelector(`[data-panel-menu-item-key="${activeMenuItem.key}"]`);
      if (target) {
        const rect = target.getBoundingClientRect();
        const isVisible = rect.top >= 0 && rect.bottom <= window.innerHeight;

        if (!isVisible) {
          target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    });
  } else if (currentSelection) {
    // Only clear selection if current selection is truly invalid
    const allMenuItems = menuItemsWithKeys.value.flatMap((item) => [item, ...(item.items || [])]);
    const isCurrentSelectionValid = allMenuItems.some((item) => item.key === currentSelection.key);

    if (!isCurrentSelectionValid) {
      internalSelected.value = [];
    }
  }
}

onBeforeMount(() => {
  expandedKeys.value = initExpandedKeys();
});

onMounted(() => {
  expandItemMatchingRoute();
});

watch(
  () => [route.name, route.params],
  () => {
    expandItemMatchingRoute();
  },
  { immediate: true },
);

watch(
  () => props.loading,
  () => {
    expandItemMatchingRoute();
  },
);

const { expanded: sideNavExpanded } = useSecondaryNavigationPanel();

watch(
  () => sideNavExpanded.value,
  (expanded) => {
    if (expanded) {
      expandItemMatchingRoute();
    }
  },
);

watch(
  () => props.selected,
  (selected) => {
    internalSelected.value = selected;
  },
  { immediate: true },
);

watch(
  () => internalSelected?.value,
  (selected) => {
    if (!isEqual(selected, props.selected)) {
      emit('update:selected', selected);
    }
  },
);

// Watch for changes in menu items and re-expand when they're updated
watch(
  () => menuItemsWithKeys?.value,
  (newMenuItems) => {
    if (newMenuItems.length > 0) {
      expandItemMatchingRoute();
    }
  },
  { flush: 'post' },
);
</script>

<template>
  <NavSecondaryMenu
    v-model:selected="internalSelected"
    v-model:expanded-keys="expandedKeys"
    class="cy-test--secondary-navigation-menu"
    :menu-items="menuItemsWithKeys"
    :loading="props.loading"
  />
</template>
